
import { Box, Link, Stack, Typography } from "@mui/material";

import { gdocEmbedUrl, gdocShareableUrl } from "./embed-utils";

import type { WidgetProps } from "./types";

// Embed a Google Doc inside the room. The doc must be shared with
// "Anyone with the link can edit" (writer) for the inline iframe editor to
// be usable — `/personalize-partnership-form` sets this automatically when
// it copies the template per-supplier.
//
// Under the frame sits one small, centred, gray text link that opens the
// doc in a new tab (Bill, 2026-09-04), so the supplier can always reach the
// original document. Nothing else renders there: the helper / "Copy link"
// card that used to sit under the frame (and carried the personalize-pending
// marker) was removed the same day. `helperText` stays in the schema for
// rooms written before the change but is not rendered.
export function GDocEmbed({ widget }: WidgetProps<"gdoc_embed">) {
    const { url, title, height } = widget.props;
    const src = gdocEmbedUrl(url);
    const shareUrl = gdocShareableUrl(url);
    const h = height ?? 720;

    // `height` is the frame's natural height. Inside a height-constrained
    // column (BasePage `fillViewport`) the frame flexes to whatever is left,
    // so the doc's own scrollbar is the only one; elsewhere it stays at `height`.
    return (
        // 4px between the frame and the link (was 8px, Bill 2026-09-05), so the
        // space under the doc is the link line plus the page's 8px bottom gutter.
        <Stack spacing={0.5} sx={{ flex: 1, minHeight: 0 }}>
            {title && <Typography variant="h5">{title}</Typography>}
            <Box
                component="iframe"
                src={src}
                title={title ?? "Google Doc"}
                sx={{
                    width: "100%",
                    height: h,
                    flex: "1 1 auto",
                    minHeight: 0,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "10px",
                    bgcolor: "background.paper",
                }}
            />
            <Link
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                underline="hover"
                sx={{ alignSelf: "center", color: "text.secondary", fontWeight: 400 }}
            >
                Open this document in a new tab
            </Link>
        </Stack>
    );
}
